import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const NAV = [
  { to: "/", label: "Beranda" },
  { to: "/paket-umrah", label: "Paket Umrah" },
  { to: "/haji-khusus", label: "Haji Khusus" },
  { to: "/e-course", label: "E-Course" },
  { to: "/toko", label: "Toko Online" },
  { to: "/tentang-kami", label: "Tentang Kami" },
  { to: "/artikel", label: "Artikel" },
  { to: "/cek-booking", label: "Cek Booking" },
  { to: "/kontak", label: "Hubungi Kami" },
];

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const { user } = useAuth();
  const nav = useNavigate();

  const loadSettings = useCallback(() => {
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  return (
    <header className="sticky top-0 z-50 border-b border-[#E5DEC9] bg-[#FBF9F4]/95 backdrop-blur-md">
      <div className="hidden bg-[#0A0A0A] px-6 py-2 text-xs text-[#F5F1E8] md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <span className="font-cinzel text-[#D4AF37]">{settings.tagline || "Melayani Tamu Allah dengan Amanah"}</span>
          <a href={`https://wa.me/${settings.whatsapp || ""}`} target="_blank" rel="noreferrer"
            data-testid="topbar-whatsapp" className="flex items-center gap-2 hover:text-[#E5C358]">
            <Phone size={12} /> {settings.phone || "-"}
          </a>
        </div>
      </div>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
        <Link to="/" data-testid="site-logo" className="flex items-center">
          <img src="/logo.png" alt="Royal Saudi Haramain" className="h-9 w-auto sm:h-11" />
        </Link>
        <nav className="hidden items-center gap-5 xl:flex">
          {NAV.slice(1).map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#C5A028]" : "text-[#4A4844] hover:text-[#141414]"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/paket-umrah")} data-testid="site-search-btn"
            className="hidden rounded-full p-2 text-[#4A4844] hover:bg-[#F4EFE6] md:block"><Search size={18} /></button>
          <Link to={user ? "/pilih-portal" : "/login"} data-testid="site-portal-btn"
            className="btn-gold rounded-xl px-4 py-2 text-sm font-bold">
            {user ? "Portal" : "Login"}
          </Link>
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle"
            className="rounded-lg p-2 text-[#141414] xl:hidden">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="border-t border-[#E5DEC9] bg-white px-5 py-3 xl:hidden" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
              className="block border-b border-[#F4EFE6] py-3 text-sm text-[#121212]">{n.label}</Link>
          ))}
        </div>
      )}
    </header>
  );
};

export const SiteFooter = () => {
  const [s, setS] = useState({});
  const loadSettings = useCallback(() => {
    api.get("/public/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);
  return (
    <footer className="mt-24 bg-[#0A0A0A] text-[#F5F1E8]" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 md:grid-cols-4">
        <div>
          <img src="/logo.png" alt="Royal Saudi Haramain" className="h-12 w-auto" />
          <p className="mt-4 font-cinzel text-sm text-[#D4AF37]">{s.tagline}</p>
          <p className="mt-3 text-sm text-[#C7C0B2]">royalsaudi.id</p>
        </div>
        <div>
          <h4 className="eyebrow">Layanan</h4>
          <ul className="mt-4 space-y-2 text-sm text-[#C7C0B2]">
            <li><Link to="/paket-umrah">Paket Umrah</Link></li>
            <li><Link to="/haji-khusus">Haji Khusus</Link></li>
            <li><Link to="/e-course">E-Course</Link></li>
            <li><Link to="/toko">Toko Online</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="eyebrow">Informasi</h4>
          <ul className="mt-4 space-y-2 text-sm text-[#C7C0B2]">
            <li><Link to="/tentang-kami">Tentang Kami</Link></li>
            <li><Link to="/artikel">Artikel & Panduan</Link></li>
            <li><Link to="/cek-booking">Cek Booking</Link></li>
            <li><Link to="/kontak">Hubungi Kami</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="eyebrow">Kontak</h4>
          <ul className="mt-4 space-y-2 text-sm text-[#C7C0B2]">
            <li>{s.address}</li>
            <li>{s.phone}</li>
            <li>{s.email}</li>
          </ul>
          <a href={`https://wa.me/${s.whatsapp || ""}`} target="_blank" rel="noreferrer"
            data-testid="footer-whatsapp" className="btn-gold mt-4 inline-block rounded-xl px-4 py-2 text-sm font-bold">
            Chat WhatsApp
          </a>
        </div>
      </div>
      <div className="border-t border-white/10 px-6 py-5 text-center text-xs text-[#8D8677]">
        © {new Date().getFullYear()} Royal Saudi Haramain. Semua harga dalam Rupiah (IDR), zona waktu Asia/Jakarta.
      </div>
    </footer>
  );
};

export const SiteLayout = ({ children }) => (
  <div className="min-h-screen bg-[#FBF9F4]">
    <SiteHeader />
    <main>{children}</main>
    <SiteFooter />
  </div>
);
